import { useEffect, useState } from 'react'
import { Box, Stack, Typography, Divider, LinearProgress, Link, } from '@mui/material'
import { ReactComponent as AttachFileIcon } from '@assets/images/icon/ico_download.svg'
import { useIsMobile } from '@/hooks/Responsive'
import DOMPurify from 'dompurify';
import { downloadFile } from '@/api/util';

export default function BoardView({
  fetcher,
  row,
  renderTitle,
  renderMeta,
  renderContent,
  renderAttachments,
  bottomToolbarRender,
}) {
  const { isMobile } = useIsMobile()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const cleanHtml = (text) => {
    const clean = DOMPurify.sanitize(text, {
          USE_PROFILES: { html: true },
        })
      return clean;
  }
  // 데이터 로드
  useEffect(() => {
    let aborted = false
    async function run() {
      if (!fetcher) {
        setData(row)
        return
      }
      setLoading(true)
      try {
        const res = await fetcher()
        if (!aborted) setData(res)
      } finally {
        if (!aborted) setLoading(false)
      }
    }
    run()
    return () => {
      aborted = true
    }
  }, [fetcher, row])

  const hasAnswer = Boolean(data?.answer || data?.reply)

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Stack>
    )
  }

  if (!data) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <Typography color="text.secondary">데이터가 없습니다.</Typography>
      </Stack>
    )
  }

  const DefaultTitle = () => (
    <Typography component="div" variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700}>
      {data.title}
    </Typography>
  )

  const rawMeta = [
    data.qInstitution && ['기관', data.qInstitution],
    data.qDepartment && ['학과', data.qDepartment],
    (data.qCategory && !data.qLecture) && ['문의', data.qCategory],
    (data.qCategory && data.qLecture) && [
      '문의',
      data.qCategory && data.qLecture
        ? `${data.qCategory} > ${data.qLecture}`
        : data.qCategory || data.qLecture,
    ],
    data.writer && ['등록자', data.writer],
  ].filter(Boolean)

  const infoLine = rawMeta.map(([label, value], idx) => (
    <Stack
      key={idx}
      direction="row"
      alignItems="center"
      spacing={0.5}
    >
      <Typography component="span" variant="body2" fontWeight={500} color="text.secondary">
        {label}
      </Typography>

      <Box
        sx={{
          width: '1px',
          height: '12px',
          bgcolor: 'grey.300',
        }}
      />

      <Typography component="span" variant="body2" color="text.secondary">
        {value}
      </Typography>
    </Stack>
  ))

  const hasInfoLine = infoLine && infoLine.length > 0
  
  const DefaultMeta = () => {
    // 확장 가능
    const metaItems = [
      ...(infoLine
        ? [
            {
              label: '',
              value: (
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={isMobile? 1 : 2}
                >
                  {infoLine}
                </Stack>
              ),
            },
          ]
        : []),

      { label: '등록일', value: data.createdAt || data.date },

      ...(data.category
        ? [{ label: '분류', value: data.category }] : []),
        
      ...(!hasInfoLine && (data.qCategory || data.qLecture)
      ? [
          {
            label: data.qCategory ? '분류' : '강좌',
            value: (
              <Typography variant="body2" component="span">
                {data.qCategory || data.qLecture}
                {data.qCategory && data.qLecture && ` > ${data.qLecture}`}
              </Typography>
            ),
          },
        ]
      : []),

      ...(!hasInfoLine && data.writer
        ? [{ label: '등록자', value: data.writer }]
        : []),

      ...(data.views ? [{ label: '조회수', value: data.views }] : []),
    ]
    return (
      <Stack
        spacing={1}
        sx={{ color: 'text.secondary', typography: 'body2', mt: 2 }}
      >
        {metaItems.map((item, idx) => (
          <Stack
            key={idx}
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            {item.label && (
              <>
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight={500}
                  color="text.secondary"
                >
                  {item.label}
                </Typography>

                <Box
                  sx={{
                    width: '1px',
                    height: '12px',
                    bgcolor: 'grey.300',
                  }}
                />
              </>
            )}

            <Typography component="span" variant="body2" color="text.secondary">
              {item.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )
  }

  const DefaultAttachments = () =>
    Array.isArray(data.attachments) && data.attachments.length > 0 ? (
      <Stack direction={isMobile ? 'column' : 'row'} alignItems="flex-start" spacing={0.5} sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            첨부파일
          </Typography>
          <Box sx={{ width: '1px', height: '12px', bgcolor: 'grey.300', display: isMobile ? 'none' : 'block' }} />
        </Stack>

        <Stack spacing={0.5}>
          {data.attachments.map((file, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={0.5}
              sx={{ typography: 'body2', color: 'text.secondary' }}
            >
              <AttachFileIcon sx={{ flexShrink: 0, color: 'grey.dark' }} />
              <Link
                // href={downloadFile(file.url, file.name)}
                onClick={() =>
                  downloadFile(
                    file.url,
                    file.name
                  )
                }
                underline="hover"
                sx={{
                  color: 'text.secondary',
                  flex: 1,
                  cursor: "pointer"
                }}
              >
                {file.name}
              </Link>
            </Stack>
          ))}
        </Stack>
      </Stack>
    ) : null

  const DefaultContent = () => (
    <Typography sx={{ whiteSpace: 'pre-line' }}>
      {data.content}
    </Typography>
  )

  return (
    <Box>
      <Divider sx={{ borderColor: 'primary.main', borderTopWidth: 1 }} />

      <Stack sx={{ px: isMobile ? 0 : 3, pt: 4, pb: 7 }}>
        {/* 제목 */}
        {renderTitle ? renderTitle(data) : <DefaultTitle />}

        {/* 메타 */}
        {renderMeta ? renderMeta(data) : <DefaultMeta />}

        {/* 첨부파일 */}
        {renderAttachments ? renderAttachments(data) : <DefaultAttachments />}

        <Divider sx={{ mt: 3, mb: 6 }} />

        {/* 본문 */}
        {data?.content ? <Box dangerouslySetInnerHTML={{ __html: cleanHtml(data.content) }} /> : <DefaultContent />}

        {/* 관리자 답변 */}
        {hasAnswer && (
          <>
            <Divider sx={{ mt: 6, color: 'divider' }} />
            <Stack
              sx={{
                mt: 3,
                p: 3,
                bgcolor: 'primary.50',
                borderRadius: 4,
              }}
            >
              <Typography fontWeight={600}>관리자</Typography>

              {/* 답변 내용 */}
              <Typography sx={{ mt: 1.5 }}>{data.answer || data.reply ? <Box dangerouslySetInnerHTML={{ __html: cleanHtml(data.answer) }} /> : data.answer || data.reply}</Typography>

              {/* 첨부파일 */}
              {Array.isArray(data.answerattachments) && data.answerattachments.length > 0 && (
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  alignItems="flex-start"
                  spacing={0.5}
                  sx={{ mt: 3 }}
                >
                  {/* 제목 + 구분선 */}
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">
                      첨부파일
                    </Typography>
                    <Box
                      sx={{
                        width: '1px',
                        height: '12px',
                        bgcolor: 'grey.300',
                        display: isMobile ? 'none' : 'block',
                      }}
                    />
                  </Stack>

                  {/* 첨부파일 리스트 */}
                  <Stack spacing={0.5}>
                    {data.answerattachments.map((file, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        spacing={0.5}
                        sx={{ typography: 'body2', color: 'text.secondary' }}
                      >
                        <AttachFileIcon sx={{ flexShrink: 0, color: 'grey.dark' }} />
                        <Link
                          // href={file.url}
                          onClick={() =>
                            downloadFile(
                              file.url,
                              file.name
                            )
                          }
                          underline="hover"
                          sx={{
                            color: 'text.secondary',
                            flex: 1,
                            cursor: "pointer"
                          }}
                        >
                          {file.name}
                        </Link>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </>
        )}

        {/* 답변 대기 */}
        {!hasAnswer && data?.isQna && (
          <>
            <Divider sx={{ mt: 6, color: 'divider' }} />
            <Box sx={{ 
              mt: 3,
              p: 3,
              bgcolor: 'primary.50',
              borderRadius: 4,
            }}>
              <Typography>답변 대기중입니다.</Typography>
            </Box>
          </>
        )}
      </Stack>

      <Divider sx={{ mb: 5 }} />

      {/* 하단 버튼 */}
      {bottomToolbarRender ? bottomToolbarRender({ data }) : null}
    </Box>
  )
}
